// Extend Error to include captureStackTrace
interface ErrorConstructor {
  captureStackTrace?(target: object, constructorOpt?: Function): void;
}

export class EvolutionApiError extends Error {
  public readonly statusCode?: number;
  public readonly details?: unknown;

  constructor(message: string, cause?: unknown, statusCode?: number) {
    const extractedMessage = extractErrorMessage(cause);
    const finalMessage = extractedMessage || message || 'Unknown error occurred';

    super(finalMessage);

    this.name = EvolutionApiError.name;
    this.message = finalMessage;
    this.statusCode = statusCode;
    this.details = cause;

    // Maintain proper stack trace
    const ErrorConstructor = Error as ErrorConstructor;
    if (typeof ErrorConstructor.captureStackTrace === 'function') {
      ErrorConstructor.captureStackTrace(this, EvolutionApiError);
    }
  }

  /**
   * Returns a user-friendly string representation of the error
   */
  toString(): string {
    let result = `${this.name}: ${this.message}`;

    if (this.statusCode) {
      result += ` (${this.statusCode})`;
    }

    // Add relevant details without showing [Object ...]
    if (this.details && typeof this.details === 'object') {
      const details = this.details as Record<string, unknown>;
      const relevantDetails: string[] = [];

      if (typeof details.url === 'string') {
        relevantDetails.push(`URL: ${details.url}`);
      }

      if (typeof details.method === 'string') {
        relevantDetails.push(`Method: ${details.method}`);
      }

      // Show response details if they exist and are meaningful
      if (details.response && typeof details.response === 'object') {
        const response = details.response as Record<string, unknown>;
        if (typeof response.error === 'string' && response.error !== this.message) {
          relevantDetails.push(`Server Error: ${response.error}`);
        }
        if (typeof response.message === 'string' && response.message !== this.message) {
          relevantDetails.push(`Server Message: ${response.message}`);
        }
      }

      if (relevantDetails?.length > 0) {
        result += `\n  ${relevantDetails.join('\n  ')}`;
      }
    }

    return result;
  }

  /**
   * Returns a JSON representation suitable for logging
   */
  toJSON(): object {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      details: this.details,
      stack: this.stack,
    };
  }
}

/**
 * Helper to safely access nested properties
 */
function getNestedProperty(obj: unknown, path: string[]): unknown {
  let current: unknown = obj;
  for (const key of path) {
    if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Extracts error message from various Evolution API error response formats
 */
export function extractErrorMessage(response: unknown): string | null {
  if (!response) {
    return null;
  }

  // Handle string responses
  if (typeof response === 'string') {
    return response;
  }

  // Handle object responses
  if (typeof response === 'object' && response !== null) {
    try {
      const errorObj = response as Record<string, unknown>;

      // Try different common error message paths, prioritizing Evolution API format
      const checkPaths: Array<() => string | null> = [
        // Evolution API specific nested structure (most common)
        () => {
          const val = getNestedProperty(errorObj, ['response', 'response', 'message']);
          if (Array.isArray(val) && val.length > 0) return String(val[0]);
          return null;
        },
        () => {
          const val = getNestedProperty(errorObj, ['response', 'response', 'error']);
          return typeof val === 'string' ? val : null;
        },
        () => {
          const val = getNestedProperty(errorObj, ['response', 'response', 'description']);
          return typeof val === 'string' ? val : null;
        },

        // Evolution API first level nested
        () => {
          const val = getNestedProperty(errorObj, ['response', 'message']);
          if (Array.isArray(val) && val.length > 0) return String(val[0]);
          return null;
        },
        () => {
          const val = getNestedProperty(errorObj, ['response', 'error']);
          return typeof val === 'string' ? val : null;
        },
        () => {
          const val = getNestedProperty(errorObj, ['response', 'description']);
          return typeof val === 'string' ? val : null;
        },

        // Direct error message
        () => {
          const val = errorObj.message;
          if (Array.isArray(val) && val.length > 0) return String(val[0]);
          return typeof val === 'string' ? val : null;
        },
        () => {
          const val = errorObj.error;
          return typeof val === 'string' ? val : null;
        },
        () => {
          const val = errorObj.description;
          return typeof val === 'string' ? val : null;
        },
        () => {
          const val = errorObj.detail;
          return typeof val === 'string' ? val : null;
        },

        // Other nested error messages
        () => {
          const val = getNestedProperty(errorObj, ['data', 'error']);
          return typeof val === 'string' ? val : null;
        },
        () => {
          const val = getNestedProperty(errorObj, ['data', 'message']);
          return typeof val === 'string' ? val : null;
        },

        // Array format messages (fallback)
        () => {
          const val = errorObj.error;
          if (Array.isArray(val) && val.length > 0) return String(val[0]);
          return null;
        },
        () => {
          const val = errorObj.errors;
          if (Array.isArray(val) && val.length > 0) return String(val[0]);
          return null;
        },
      ];

      for (const checkPath of checkPaths) {
        const result = checkPath();
        if (result && result.trim().length > 0) {
          return result.trim();
        }
      }

      // Handle validation errors (common format)
      if (errorObj.validation && Array.isArray(errorObj.validation)) {
        const validationErrors = errorObj.validation
          .map((v: unknown) => {
            if (v && typeof v === 'object') {
              const vObj = v as Record<string, unknown>;
              return vObj.message || vObj.error || String(v);
            }
            return String(v);
          })
          .filter(Boolean)
          .join(', ');
        if (validationErrors) {
          return `Validation error: ${validationErrors}`;
        }
      }

      // Handle specific Evolution API error patterns
      if (typeof errorObj.statusCode === 'number' && typeof errorObj.statusText === 'string') {
        return `${errorObj.statusCode}: ${errorObj.statusText}`;
      }

      // Last resort: try to stringify the object meaningfully
      const keys = Object.keys(errorObj);
      if (keys && keys.length > 0) {
        try {
          return JSON.stringify(errorObj);
        } catch {
          return '[Complex error object]';
        }
      }
    } catch (error) {
      // If any error occurs during extraction, return a safe message
      return '[Error parsing error message]';
    }
  }

  return null;
}

/**
 * Legacy error message patterns for specific Evolution API errors
 * Kept for backward compatibility
 */
interface ErrorPattern {
  pattern: (obj: unknown) => boolean;
  message: string | ((obj: unknown) => string);
}

const SpecificErrorPatterns: ErrorPattern[] = [
  {
    pattern: (obj: unknown) => {
      if (!obj || typeof obj !== 'object') return false;
      const o = obj as Record<string, unknown>;
      return (
        Array.isArray(o.message) &&
        o.message.some((m: unknown) => {
          if (!m || typeof m !== 'object') return false;
          const mObj = m as Record<string, unknown>;
          return mObj.exists === false && 'jid' in mObj && 'number' in mObj;
        })
      );
    },
    message: 'Provided number is not a valid WhatsApp number',
  },
  {
    pattern: (obj: unknown) => {
      if (!obj || typeof obj !== 'object') return false;
      const o = obj as Record<string, unknown>;
      return (
        Array.isArray(o.message) &&
        o.message.some((m: unknown) => typeof m === 'string' && m.includes('Media upload failed'))
      );
    },
    message: 'Media upload failed on all hosts',
  },
  {
    pattern: (obj: unknown) => {
      if (!obj || typeof obj !== 'object') return false;
      const o = obj as Record<string, unknown>;
      return (
        Array.isArray(o.message) &&
        o.message.some((m: unknown) => typeof m === 'string' && m.includes('No session'))
      );
    },
    message: 'No session found, try restarting your instance',
  },
  {
    pattern: (obj: unknown) => {
      if (!obj || typeof obj !== 'object') return false;
      const o = obj as Record<string, unknown>;
      return (
        Array.isArray(o.message) &&
        o.message.some((m: unknown) => typeof m === 'string' && m.includes('AggregateError'))
      );
    },
    message: 'Connection error occurred',
  },
  {
    pattern: (obj: unknown) => {
      if (!obj || typeof obj !== 'object') return false;
      const o = obj as Record<string, unknown>;
      return (
        Array.isArray(o.message) &&
        o.message.some((m: unknown) => typeof m === 'string' && m.includes('AxiosError'))
      );
    },
    message: (obj: unknown) => {
      if (!obj || typeof obj !== 'object') return 'Network error occurred';
      const o = obj as Record<string, unknown>;
      if (Array.isArray(o.message) && o.message.length > 0) {
        return String(o.message[0]);
      }
      return 'Network error occurred';
    },
  },
];

/**
 * Enhanced error message extraction with specific pattern matching
 */
function getErrorMessage(response: unknown): string | null {
  // First try the general extraction
  const generalMessage = extractErrorMessage(response);
  if (generalMessage) {
    return generalMessage;
  }

  // Then try specific patterns
  if (typeof response === 'object' && response !== null) {
    for (const { pattern, message } of SpecificErrorPatterns) {
      try {
        if (pattern(response)) {
          return typeof message === 'string' ? message : message(response);
        }
      } catch {
        // Continue to next pattern if this one fails
      }
    }
  }

  return null;
}
